"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Check, MoveRight } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { SITE_CONFIG } from "@/lib/constants";
import {
  monthAvailability,
  formatLongDate,
  FORMAT_LABEL,
  type Availability,
} from "@/lib/availability";

const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MAX_AHEAD = 3; // current month + 3

interface Selected {
  year: number;
  month: number;
  day: number;
  format: Availability;
}

interface BookingCalendarProps {
  /** Stable seed: a location slug, or "team" for the generic team page. */
  seed: string;
  /** Where "Request this date" sends people; a query string is appended before any #hash. */
  enquiryHref: string;
  /** Shown in the confirmation + sent through as ?location=. */
  locationName?: string;
  /** Team page: in-person days vary by the client's location, so flag them as indicative. */
  indicativeInPerson?: boolean;
  className?: string;
}

function buildRequestHref(
  base: string,
  params: Record<string, string>,
): string {
  const [path, hash] = base.split("#");
  const qs = new URLSearchParams(params).toString();
  const sep = path.includes("?") ? "&" : "?";
  return `${path}${sep}${qs}${hash ? `#${hash}` : ""}`;
}

/**
 * A booking-style availability calendar. Online sessions run ~half of weekdays;
 * in-person full days are two or three a month and depend on the location.
 * Dates are generated deterministically (see lib/availability), computed on the
 * client so "today" is always current without a hydration mismatch. Picking a
 * date sends the choice to the enquiry form.
 */
export function BookingCalendar({
  seed,
  enquiryHref,
  locationName,
  indicativeInPerson = false,
  className,
}: BookingCalendarProps) {
  const [mounted, setMounted] = useState(false);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<Selected | null>(null);

  useEffect(() => setMounted(true), []);

  // Base month = current month + offset (client-only, so it stays current).
  const { year, month, today } = useMemo(() => {
    const now = new Date();
    const base = new Date(now.getFullYear(), now.getMonth() + offset, 1);
    const t = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    return { year: base.getFullYear(), month: base.getMonth(), today: t };
  }, [offset]);

  const avail = useMemo(
    () => monthAvailability(seed, year, month),
    [seed, year, month],
  );

  if (!mounted) {
    // Stable placeholder to avoid layout shift / hydration mismatch.
    return (
      <div
        className={cn(
          "rounded-md border border-line bg-paper p-5 shadow-sm",
          className,
        )}
      >
        <div className="h-[22rem] animate-pulse rounded bg-light-bg" aria-hidden="true" />
        <p className="sr-only">Loading available dates…</p>
      </div>
    );
  }

  const firstDow = new Date(year, month, 1).getDay(); // 0 Sun … 6 Sat
  const lead = (firstDow + 6) % 7; // Monday-start offset
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (number | null)[] = [
    ...Array.from({ length: lead }, () => null),
    ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
  ];
  while (cells.length % 7 !== 0) cells.push(null);

  const selectedDate =
    selected && new Date(selected.year, selected.month, selected.day);

  const requestHref =
    selected &&
    buildRequestHref(enquiryHref, {
      date: `${selected.year}-${String(selected.month + 1).padStart(2, "0")}-${String(selected.day).padStart(2, "0")}`,
      format: selected.format,
      ...(locationName ? { location: locationName } : {}),
    });

  return (
    <div
      className={cn(
        "rounded-md border border-line bg-paper p-5 shadow-sm",
        className,
      )}
    >
      {/* Month navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setOffset((o) => Math.max(0, o - 1))}
          disabled={offset === 0}
          className="inline-flex size-11 items-center justify-center rounded-md border border-line text-mid transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
          aria-label="Previous month"
        >
          <ChevronLeft className="size-4" aria-hidden="true" />
        </button>
        <p className="font-heading text-base font-semibold text-dark" aria-live="polite">
          {MONTHS[month]} {year}
        </p>
        <button
          type="button"
          onClick={() => setOffset((o) => Math.min(MAX_AHEAD, o + 1))}
          disabled={offset === MAX_AHEAD}
          className="inline-flex size-11 items-center justify-center rounded-md border border-line text-mid transition-colors hover:text-primary disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary"
          aria-label="Next month"
        >
          <ChevronRight className="size-4" aria-hidden="true" />
        </button>
      </div>

      {/* Legend */}
      <div className="mt-4 flex flex-wrap gap-4 text-xs text-mid">
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-primary" aria-hidden="true" />
          Online session
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="size-2.5 rounded-full bg-secondary" aria-hidden="true" />
          In-person full day
        </span>
      </div>

      {/* Weekday headers */}
      <div className="mt-4 grid grid-cols-7 gap-1 text-center text-[11px] font-semibold uppercase tracking-wide text-mid">
        {WEEKDAYS.map((w) => (
          <div key={w} className="py-1">
            {w}
          </div>
        ))}
      </div>

      {/* Day grid */}
      <div className="mt-1 grid grid-cols-7 gap-1">
        {cells.map((d, i) => {
          if (d === null) return <div key={`e${i}`} aria-hidden="true" />;
          const date = new Date(year, month, d);
          const isPast = date < today;
          const a = avail.get(d);

          if (!a || isPast) {
            return (
              <div
                key={d}
                className={cn(
                  "flex aspect-square min-h-[44px] items-center justify-center rounded-md text-sm",
                  isPast ? "text-mid/50" : "text-mid/70",
                )}
              >
                {d}
              </div>
            );
          }

          const isSelected =
            selected &&
            selected.year === year &&
            selected.month === month &&
            selected.day === d;

          return (
            <button
              key={d}
              type="button"
              onClick={() =>
                setSelected({ year, month, day: d, format: a })
              }
              aria-pressed={isSelected || undefined}
              aria-label={`${formatLongDate(date)}, ${FORMAT_LABEL[a]}`}
              className={cn(
                "relative flex aspect-square min-h-[44px] flex-col items-center justify-center rounded-md border text-sm font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-secondary",
                a === "online"
                  ? "border-primary/40 bg-primary/5 text-primary hover:bg-primary/10"
                  : "border-secondary/50 bg-secondary/10 text-secondary hover:bg-secondary/20",
                isSelected && "ring-2 ring-dark ring-offset-1",
              )}
            >
              {d}
              <span
                className={cn(
                  "mt-0.5 size-1.5 rounded-full",
                  a === "online" ? "bg-primary" : "bg-secondary",
                )}
                aria-hidden="true"
              />
            </button>
          );
        })}
      </div>

      {indicativeInPerson && (
        <p className="mt-4 text-xs text-mid">
          In-person days are indicative; exact availability depends on your
          location and is confirmed when you enquire.
        </p>
      )}

      {/* Selection + request */}
      {selected && selectedDate && requestHref ? (
        <div className="mt-5 flex flex-col gap-3 rounded-md border border-secondary/40 bg-secondary/[0.06] p-4 sm:flex-row sm:items-center sm:justify-between">
          <p className="flex items-start gap-2 text-sm text-dark">
            <Check className="mt-0.5 size-4 shrink-0 text-success" aria-hidden="true" />
            <span>
              <strong className="font-semibold">{FORMAT_LABEL[selected.format]}</strong>
              {", "}
              {formatLongDate(selectedDate)}
              {locationName ? ` in ${locationName}` : ""}
            </span>
          </p>
          <Button asChild size="sm" className="shrink-0">
            <a href={requestHref}>
              Request this date
              <MoveRight className="size-4" aria-hidden="true" />
            </a>
          </Button>
        </div>
      ) : (
        <p className="mt-5 text-sm text-mid">
          Pick an available date to request it. Prefer to talk first? Call us
          free on{" "}
          <a
            href={`tel:${SITE_CONFIG.parentCompany.phone.replace(/\s+/g, "")}`}
            className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
          >
            {SITE_CONFIG.parentCompany.phone}
          </a>{" "}
          or{" "}
          <a
            href={enquiryHref}
            className="font-semibold text-primary underline underline-offset-2 hover:text-secondary"
          >
            send an enquiry
          </a>
          .
        </p>
      )}
    </div>
  );
}
